import { createContext, useContext, useEffect, useState } from 'react'
import { authenticationAPI } from '../apis'
import { getTokenPayload } from '../utils/jwt-util';
import { AlertColor } from '@mui/material';

/**
 * Interface for the user context
 */
export interface IUser {
  Entities: unknown;
  id?: string,
  email?: string
  name: string
  mobile: string
  OrganizationId: string
  role: string
  entityId: string | null | undefined
  EntityId?: string | null | undefined
  isSystemGeneratedPassword?: boolean | undefined
}

/**
 * Interface for the Auth context (from useAuth)
 */
export interface IAuthContext {
  isAuthenticated: boolean
  isLoading: boolean
  isSessionExpired: boolean
  error: string | null
  user: IUser | null
  signIn: (username: string, password: string) => Promise<any>
  verifyOTP: (email: string, otp: string) => Promise<any>
  signOut: () => void
  setSessionExpired: (value: boolean, message: string) => void
  isSystemGeneratedPassword: boolean
  setSystemGeneratedPassword: (value: boolean) => void

  showSnackbar: boolean,
  snackbarMessage: string | null
  snackbarSeverity: AlertColor
  onShowSnackbar: (message: string, severity: AlertColor) => void
  onHideSnackbar: () => void

  appTitle: string | null
  setAppTitle: (message: string) => void
  setIsLoading: (value: boolean) => void
  updateUser: (updatedFields: Partial<IUser>) => void

}

export const authContext = createContext<IAuthContext>({} as IAuthContext)

const useProvideAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(true)
  const [isSystemGeneratedPassword, setIsSystemGeneratedPassword] = useState<boolean>(false)
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [user, setUser] = useState<IUser | null>(null)

  const [showSnackbar, setShowSnackbar] = useState<boolean>(false)
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");
  const [pageTitle, setPageTitle] = useState<string | null>(null);


  useEffect(() => {
    const accessToken = localStorage.getItem('access-token');
    const loggedInUserDetails = localStorage.getItem('loggedInUser');
    if (accessToken && loggedInUserDetails) {
      try {
        const jwtPayload = getTokenPayload(accessToken)
        setIsAuthenticated(true)
        setUser(JSON.parse(loggedInUserDetails));
      } catch (err) {
        setIsAuthenticated(false)
        setUser(null)
      }
    } else {
      setIsAuthenticated(false)
      setUser(null)
    }
  }, []);

  const updateLoggedInUserDetails = (userDetails: IUser) => {
    let userData = Object.assign({}, userDetails);
    // userData.role = ROLES.ENTITY_ADMIN;
    userData.entityId = userData?.EntityId || "";
    localStorage.setItem('loggedInUser', JSON.stringify(userData))
    return userData;
  };

  const signIn = async (username: string, password: string) => {
    try {
      setIsLoading(true)
      const res = await authenticationAPI.signIn({
        username,
        password,
      });
      let userData = updateLoggedInUserDetails(res?.data?.user);
      setIsAuthenticated(true)
      setError(null)
      setIsSessionExpired(false);
      setIsSystemGeneratedPassword(false);
      setIsLoading(false)
      setSystemGeneratedPassword(userData?.isSystemGeneratedPassword || false);
      setUser(userData)

      return res
    } catch (err: any) {
      setIsAuthenticated(false);
      setError(err?.response?.data?.error);
    } finally {
      setIsLoading(false)
    }
  }

  const verifyOTP = async (email: string, otp: string) => {
    try {
      setIsLoading(true)
      const res = await authenticationAPI.verifyOTP({
        email,
        otp
      });
      let userData = updateLoggedInUserDetails(res?.data?.user);
      setIsAuthenticated(true)
      setUser(userData)
      setError(null)
      setIsLoading(false)
      return res
    } catch (err: any) {
      setIsAuthenticated(false)
      setError(err?.response?.data?.error);
    } finally {
      setIsLoading(false)
    }
  }

  const signOut = async () => {
    await authenticationAPI.signOut()
    setIsAuthenticated(false);
    setIsSessionExpired(false);
    setIsSystemGeneratedPassword(false);
    setError(null);
    setUser(null);
  }

  const setSessionExpired = async (value: boolean, message: string) => {
    setIsSessionExpired(value);
    setError(message);
  }

  const onShowSnackbar = async (message: string, severity: AlertColor) => {
    setShowSnackbar(true);
    setSnackbarMessage(message);
    setSnackbarSeverity(severity)
  }

  const onHideSnackbar = async () => {
    setShowSnackbar(false);
    setSnackbarMessage(null);
    setSnackbarSeverity("success")
  }

  const setAppTitle = async (title: string) => {
    setPageTitle(title);
  }

  const setSystemGeneratedPassword = async (value: boolean) => {
    setIsSystemGeneratedPassword(value);
  }

  const updateUser = (updatedFields: Partial<IUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...updatedFields };
      localStorage.setItem('loggedInUser', JSON.stringify(updated));
      return updated;
    });
  };

  return {
    isAuthenticated,
    isLoading,
    isSessionExpired,
    error,
    user,
    signIn,
    signOut,
    verifyOTP,
    setSessionExpired,
    isSystemGeneratedPassword,
    setSystemGeneratedPassword,
    setIsLoading,
    showSnackbar,
    snackbarMessage,
    snackbarSeverity,
    onShowSnackbar,
    onHideSnackbar,

    appTitle: pageTitle,
    setAppTitle,
    updateUser
  }
}


interface Props {
  children: React.ReactNode
}

export const ProvideAuth = ({ children }: Props) => {
  const auth = useProvideAuth()

  return <authContext.Provider value={auth}>{children}</authContext.Provider>
}

export const useAuth = () => {
  return useContext(authContext)
}
